import type { ManageGroup, ManageGroupId, ManagePageId, WaitingItem } from '@myrewards/contracts';
import { waitingWords } from '../home/waiting';
import { managePageAt, type ManageCounts } from './pages';

// APP-236 step 2 (plan §5): Manage's waiting counts. They are Home's Waiting on you, the very item
// list `GET /home/waiting` sends and the shell already reads for the bell, grouped by the Manage
// page each row opens. Nothing is counted by code of its own, so a count can never disagree with
// Home: a row counts once, as Home's "N waiting" counts it, on the page its own link goes to.

/**
 * The Manage page a Waiting on you row's link opens, or null for anywhere else. A record opened
 * from a Manage page is that page's: an approval is Approvals', a nomination's committee review is
 * Review's, a supervisor's decision is Spot decisions'.
 */
export function managePageOfLink(href: string): ManagePageId | null {
  const path = (href.split(/[?#]/)[0] ?? '').replace(/\/+$/, '');
  if (path === '/approvals' || path.startsWith('/approvals/')) return 'approvals';
  if (/^\/recognize\/spot\/[^/]+\/supervision\/[^/]+$/.test(path)) return 'spot-decisions';
  if (/^\/recognize\/spot\/[^/]+$/.test(path)) return 'spot-review';
  return managePageAt(path);
}

/** The Manage page one Waiting on you row opens, by the link Home shows it with. */
export function waitingPageOf(item: WaitingItem): ManagePageId | null {
  return managePageOfLink(waitingWords(item).action.href);
}

/**
 * What one row adds to its page's count: one (APP-236 D18), except the letters to hand out, which
 * count their letters (APP-245 D8 [default]), so the tab, Home's row and the page's own "to hand
 * out" say the same number.
 */
function rowWeight(item: WaitingItem): number {
  return item.type === 'letters_to_hand_out' ? item.count : 1;
}

/**
 * The counts for a person's Manage groups: each Waiting on you row once, on the page its link
 * opens, only when that page is one of theirs. A row for anywhere else (their own award, an
 * election, Settings, a page they don't have here) counts nowhere in Manage. No page or group is
 * ever counted as 0: it's left out.
 */
export function manageWaiting(
  items: readonly WaitingItem[],
  groups: readonly ManageGroup[],
): ManageCounts {
  const pages: Partial<Record<ManagePageId, number>> = {};
  const owner = new Map<ManagePageId, ManageGroupId>();
  for (const g of groups) for (const page of g.pages) owner.set(page, g.id);
  for (const item of items) {
    const page = waitingPageOf(item);
    if (page === null || !owner.has(page)) continue;
    pages[page] = (pages[page] ?? 0) + rowWeight(item);
  }
  const byGroup: Partial<Record<ManageGroupId, number>> = {};
  for (const [page, n] of Object.entries(pages) as [ManagePageId, number][]) {
    const group = owner.get(page);
    if (group !== undefined) byGroup[group] = (byGroup[group] ?? 0) + n;
  }
  return { pages, groups: byGroup };
}
